import { z } from 'zod';

/**
 * Zod helpers for the shapes form inputs actually produce.
 *
 * Inputs yield strings — including `''` for "left empty" — so numeric and date
 * fields are held as strings in form state and converted once, at submit, by
 * the functions below. Coercing inside the schema instead would turn an empty
 * box into `0`, which for a transfer fee or a wage is a real value rather than
 * "not provided".
 */

interface NumericOptions {
  min?: number;
  max?: number;
  integer?: boolean;
  message: string;
}

/** A string that is empty or a number within bounds. */
export function optionalNumeric({ min, max, integer, message }: NumericOptions) {
  return z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return false;
    if (integer && !Number.isInteger(parsed)) return false;
    if (min !== undefined && parsed < min) return false;
    if (max !== undefined && parsed > max) return false;
    return true;
  }, message);
}

/** As `optionalNumeric`, but a value is required. */
export function requiredNumeric(options: NumericOptions & { requiredMessage: string }) {
  return optionalNumeric(options).refine((raw) => raw.trim() !== '', options.requiredMessage);
}

/** `''` → undefined, otherwise the number. */
export function toNumber(raw: string | undefined): number | undefined {
  if (raw === undefined || raw.trim() === '') return undefined;
  return Number(raw);
}

/**
 * `''` → undefined, otherwise a two-decimal money string.
 *
 * Money crosses the wire as a decimal string. `toFixed(2)` is the only place it
 * converts, and the values involved are far inside float64's exact range.
 */
export function toMoney(raw: string | undefined): string | undefined {
  const value = toNumber(raw);
  return value === undefined ? undefined : value.toFixed(2);
}

/** A money string from the API as an input value: `"123500.00"` → `"123500"`. */
export function fromMoney(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : '';
}

/** `''` → undefined. Date inputs already yield `YYYY-MM-DD`, the API's date format. */
export function toDateString(raw: string | undefined): string | undefined {
  return raw && raw.trim() !== '' ? raw : undefined;
}

/** Today as `YYYY-MM-DD` in local time, for a date input's `max`. */
export function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
